import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import he from 'he';
import { debounce } from 'lodash';
import { Badge, Button, Col, Row, Tab, Tabs } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { fullName } from '../../../../../services';
import { businessActions, businessSelector, businessTeamsSelector, queryTeamMembersSelector } from '../../../../../state/modules/business';
import { currentUserSelector } from '../../../../../state/modules/user';
import { pluralise } from '../../../../../utils';
import { ControlGroup, ControlOption, Icon, SortableTable, TeamAvatar, UserAvatar } from '../../../../components';
import SearchForm from '../../../../components/search-form';
import Loading from '../../../loading';
import TaskBox from './task-box';
const CONFIG = {
    DEFAULT_TAKE: 20,
    SEARCH_DEBOUNCE_MS: 500
};
const SORT_CONFIG = {
    nameAsc: {
        displayName: 'Name',
        field: (context) => context === 'team' ? 'teamName' : 'memberName',
        order: 'asc'
    },
    selectedFirst: {
        displayName: 'Selected first',
        field: () => 'isSelected',
        order: 'desc'
    }
};
/**
 * Toggles an item in an array: removes it if present, adds it if absent.
 * Used for immutable state updates.
 * * @param array - The source array.
 * @param item - The item to add or remove.
 * @returns A new array with the item toggled.
 */
const toggleArrayItem = (array, item) => {
    return array.includes(item)
        ? array.filter((i) => i !== item)
        : [...array, item];
};
/**
 * Creates a new array containing only unique elements from the input.
 * * @param array - The input array.
 * @returns A deduplicated array.
 */
const getUniqueItems = (array) => Array.from(new Set(array));
/**
 * AddMembers Component
 * * Provides a UI for assigning teams and individual users to a task.
 * Features include:
 * - Search and sorting for members and teams.
 * - Bulk selection ("Select All") with automated background fetching.
 * - Team-based selection (selecting a team adds all its current members).
 * - Self-exclusion logic for the current user.
 */
const AddMembers = ({ task, stepTo, onTaskEdited, business, currentUser, queryMembersResults, teams, isFetchingTeams, isFetchingTeamMembers, getTeams, loadMoreTeams, getTeamMembers, loadMoreMembers }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const initialSort = task.users.value.length > 0 ? 'selectedFirst' : 'nameAsc';
    const [sort, setSort] = useState(initialSort);
    const members = useMemo(() => (queryMembersResults === null || queryMembersResults === void 0 ? void 0 : queryMembersResults.teamMembers) || [], [queryMembersResults]);
    const businessTeams = useMemo(() => (teams === null || teams === void 0 ? void 0 : teams.businessTeams) || [], [teams]);
    const totalIndividuals = useMemo(() => (queryMembersResults === null || queryMembersResults === void 0 ? void 0 : queryMembersResults.hit) ||
        (queryMembersResults === null || queryMembersResults === void 0 ? void 0 : queryMembersResults.hits) ||
        (queryMembersResults === null || queryMembersResults === void 0 ? void 0 : queryMembersResults.total) ||
        members.length, [queryMembersResults, members.length]);
    const totalTeams = (teams === null || teams === void 0 ? void 0 : teams.hit) || businessTeams.length;
    const hasMoreTeamsToLoad = businessTeams.length < totalTeams;
    const hasMoreMembersToLoad = members.length < totalIndividuals;
    const selectedUserIds = task.users.value;
    const selectedTeamIds = task.teams.value;
    const teamsFilter = useMemo(() => ({
        skip: 0,
        take: CONFIG.DEFAULT_TAKE,
        tags: searchQuery,
        sort: `${SORT_CONFIG[sort].field('team')},${SORT_CONFIG[sort].order}`
    }), [searchQuery, sort]);
    const membersFilter = useMemo(() => ({
        skip: 0,
        take: CONFIG.DEFAULT_TAKE,
        tags: searchQuery,
        sort: `${SORT_CONFIG[sort].field('member')},${SORT_CONFIG[sort].order}`
    }), [searchQuery, sort]);
    useEffect(() => {
        getTeamMembers(business.id, Object.assign(Object.assign({}, membersFilter), { selectedIds: selectedUserIds }));
        getTeams(business.id, Object.assign(Object.assign({}, teamsFilter), { selectedIds: selectedTeamIds }));
    }, []);
    const debouncedFetchRef = useRef(debounce((nextTeamsFilter, nextMembersFilter) => {
        getTeams(business.id, Object.assign(Object.assign({}, nextTeamsFilter), { selectedIds: selectedTeamIds }));
        getTeamMembers(business.id, Object.assign(Object.assign({}, nextMembersFilter), { selectedIds: selectedUserIds }));
    }, CONFIG.SEARCH_DEBOUNCE_MS));
    useEffect(() => {
        debouncedFetchRef.current(teamsFilter, membersFilter);
    }, [teamsFilter, membersFilter, business.id]);
    /**
     * Effect that watches for the completion of a "Select All" background fetch.
     * Once all members are loaded, it completes the bulk selection update.
     */
    useEffect(() => {
        if (isSelectingAll && members.length >= totalIndividuals) {
            const allMemberIds = members.map((m) => m.user.id);
            const nextSelectedUsers = getUniqueItems([
                ...selectedUserIds,
                ...allMemberIds
            ]);
            onTaskEdited('users', nextSelectedUsers);
            setIsSelectingAll(false);
        }
    }, [
        isSelectingAll,
        members,
        totalIndividuals,
        selectedUserIds,
        onTaskEdited
    ]);
    /**
     * Toggles selection state for an individual user.
     */
    const handleToggleIndividual = useCallback((userId) => {
        const nextSelectedUsers = toggleArrayItem(selectedUserIds, userId);
        onTaskEdited('users', nextSelectedUsers);
    }, [selectedUserIds, onTaskEdited]);
    /**
     * Toggles selection state for a team.
     * Selecting a team adds the team ID and all its members to the task.
     * Deselecting a team removes the team ID and all its members.
     */
    const handleToggleTeam = useCallback((teamId) => {
        const targetTeam = businessTeams.find((t) => t.team.id === teamId);
        if (!targetTeam)
            return;
        const teamMemberIds = targetTeam.members.map((user) => user.id);
        const isTeamCurrentlySelected = selectedTeamIds.includes(teamId);
        let nextSelectedUsers;
        let nextSelectedTeams;
        if (!isTeamCurrentlySelected) {
            nextSelectedUsers = getUniqueItems([
                ...selectedUserIds,
                ...teamMemberIds
            ]);
            nextSelectedTeams = getUniqueItems([...selectedTeamIds, teamId]);
        }
        else {
            nextSelectedUsers = selectedUserIds.filter((uid) => !teamMemberIds.includes(uid));
            nextSelectedTeams = selectedTeamIds.filter((tid) => tid !== teamId);
        }
        onTaskEdited('users', nextSelectedUsers);
        onTaskEdited('teams', nextSelectedTeams);
    }, [businessTeams, selectedTeamIds, selectedUserIds, onTaskEdited]);
    /**
     * Bulk toggles all individuals in the business.
     * If not all individuals are currently loaded in state, it triggers a fetch
     * for the full list before applying selection.
     */
    const handleToggleAllIndividuals = useCallback(() => {
        const allLoadedMemberIds = members.map((m) => m.user.id);
        const isAllLoadedSelected = allLoadedMemberIds.length > 0 &&
            allLoadedMemberIds.every((id) => selectedUserIds.includes(id));
        if (isAllLoadedSelected && members.length >= totalIndividuals) {
            const nextSelectedUsers = selectedUserIds.filter((id) => !allLoadedMemberIds.includes(id));
            onTaskEdited('users', nextSelectedUsers);
        }
        else if (members.length < totalIndividuals) {
            setIsSelectingAll(true);
            getTeamMembers(business.id, Object.assign(Object.assign({}, membersFilter), { skip: 0, take: totalIndividuals }));
        }
        else {
            const nextSelectedUsers = getUniqueItems([
                ...selectedUserIds,
                ...allLoadedMemberIds
            ]);
            onTaskEdited('users', nextSelectedUsers);
        }
    }, [
        members,
        selectedUserIds,
        totalIndividuals,
        business.id,
        membersFilter,
        getTeamMembers,
        onTaskEdited
    ]);
    /**
     * Triggers the API fetch for the next page of teams.
     */
    const handleLoadMoreTeams = useCallback(() => {
        if (!isFetchingTeams) {
            loadMoreTeams(business.id, Object.assign(Object.assign({}, teamsFilter), { skip: businessTeams.length, selectedIds: selectedTeamIds }));
        }
    }, [
        business.id,
        teamsFilter,
        businessTeams.length,
        selectedTeamIds,
        isFetchingTeams,
        loadMoreTeams
    ]);
    /**
     * Triggers the API fetch for the next page of members.
     */
    const handleLoadMoreMembers = useCallback(() => {
        if (!isFetchingTeamMembers) {
            loadMoreMembers(business.id, Object.assign(Object.assign({}, membersFilter), { skip: members.length, selectedIds: selectedUserIds }));
        }
    }, [
        business.id,
        membersFilter,
        members.length,
        selectedUserIds,
        isFetchingTeamMembers,
        loadMoreMembers
    ]);
    /**
     * Determines if the current logged-in user is part of a selected team.
     * Used to show the "Exclude myself" warning box.
     */
    const checkSelfIncluded = useCallback(() => {
        const teamUserIds = members
            .filter((m) => m.teamIDs.some((id) => selectedTeamIds.includes(id)))
            .map((m) => m.user.id);
        return teamUserIds.includes(currentUser.id);
    }, [members, selectedTeamIds, currentUser.id]);
    const showSelectAllRow = 'Select All'
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const isAllIndividualsSelected = members.length > 0 &&
        members.every((m) => selectedUserIds.includes(m.user.id)) &&
        members.length >= totalIndividuals;
    const selectAllIndividualRow = {
        edit: (React.createElement("div", null,
            React.createElement(ControlGroup, { id: 'edit-select-all-individuals', control: 'checkbox', type: 'styled', label: null },
                React.createElement(ControlOption, { value: 'selectAll', checked: isAllIndividualsSelected || isSelectingAll, onChange: handleToggleAllIndividuals })))),
        name: (React.createElement("div", { className: 'selectAll' },
            React.createElement("span", null, isSelectingAll
                ? 'Fetching all...'
                : `Select All ${totalIndividuals} ${pluralise('individual', totalIndividuals)}`))),
        team: null
    };
    const formattedIndividualRows = members
        .filter((member) => fullName(member.user).toLowerCase().includes(searchQuery.toLowerCase()))
        .map((member) => ({
        edit: (React.createElement("div", null,
            React.createElement(ControlGroup, { id: `edit-${member.user.id}`, control: 'checkbox', type: 'styled', label: null },
                React.createElement(ControlOption, { value: member.user.id, checked: selectedUserIds.includes(member.user.id), onChange: () => handleToggleIndividual(member.user.id) })))),
        name: (React.createElement("div", null,
            React.createElement(UserAvatar, { userName: fullName(member.user), className: undefined, avatar: member.user.avatar }),
            React.createElement("span", null, fullName(member.user)))),
        team: (React.createElement("div", null,
            member.teamNames.join(', '),
            member.teamIDs.some((id) => selectedTeamIds.includes(id)) && (React.createElement(Badge, { className: 'user-team-label' }, "Included within selected team"))))
    }));
    const individualRows = showSelectAllRow
        ? [selectAllIndividualRow, ...formattedIndividualRows]
        : formattedIndividualRows;
    const teamRows = businessTeams
        .filter((t) => t.team.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map((t) => ({
        edit: (React.createElement("div", null,
            React.createElement(ControlGroup, { id: `edit-${t.team.id}`, control: 'checkbox', type: 'styled', label: null },
                React.createElement(ControlOption, { value: t.team.id, checked: selectedTeamIds.includes(t.team.id), onChange: () => handleToggleTeam(t.team.id) })))),
        name: (React.createElement("div", null,
            React.createElement(TeamAvatar, { teamName: he.decode(t.team.name) }),
            React.createElement("span", null, he.decode(t.team.name)))),
        members: `${t.members.length} ${pluralise('member', t.members.length)}`
    }));
    if (!teams || !queryMembersResults) {
        return React.createElement(Loading, null);
    }
    return (React.createElement("div", { id: 'add-members' },
        React.createElement(TaskBox, { task: task, stepTo: stepTo }),
        React.createElement(Row, { className: 'no-gutter' },
            React.createElement(Col, { xs: 12, className: 'search-table' },
                React.createElement(Badge, { className: 'user-count' },
                    selectedTeamIds.length > 0 &&
                        `${selectedTeamIds.length} ${pluralise('team', selectedTeamIds.length)} + `,
                    `${selectedUserIds.length} ${pluralise('individual', selectedUserIds.length)} selected`),
                React.createElement("div", { className: 'search' },
                    React.createElement(SearchForm, { id: 'search-desktop', label: 'Search teams and members', className: 'searchDesktop', searchContent: undefined, searchQuery: searchQuery, onSearchChange: (e) => setSearchQuery(e.target.value), disabled: false })),
                React.createElement("div", { className: 'sort' },
                    React.createElement("span", { id: 'sort-by-text' }, "Sort by"),
                    React.createElement("select", { id: 'sort-by', value: sort, required: true, onChange: (e) => setSort(e.target.value) }, Object.keys(SORT_CONFIG).map((sortingKey) => (React.createElement("option", { key: sortingKey, value: sortingKey }, SORT_CONFIG[sortingKey].displayName))))),
                React.createElement(Tabs, { id: 'add-member-tabs', className: classNames('tab-zone', { opened: isDropdownOpen }), defaultActiveKey: 'add-team' },
                    React.createElement(Tab, { eventKey: 'add-team', title: React.createElement("div", null,
                            "Teams",
                            React.createElement("span", { className: 'activeArrow', onClick: () => setIsDropdownOpen((prev) => !prev) },
                                React.createElement("i", { className: 'icon-chevron-down-large' }))) },
                        React.createElement("form", null,
                            React.createElement(SortableTable, { className: 'teams-table', columns: [
                                    {
                                        name: 'edit',
                                        display: () => '',
                                        className: () => 'edit'
                                    },
                                    {
                                        name: 'name',
                                        display: () => 'Name',
                                        className: () => 'name'
                                    },
                                    {
                                        name: 'members',
                                        display: () => 'Team members',
                                        className: () => 'members'
                                    }
                                ], rows: teamRows }),
                            hasMoreTeamsToLoad && (React.createElement(Row, null,
                                React.createElement(Col, { xs: 4, xsOffset: 4 },
                                    React.createElement(Button, { className: 'full', onClick: handleLoadMoreTeams }, isFetchingTeams ? React.createElement(Loading, null) : 'Load More')))),
                            checkSelfIncluded() && (React.createElement(Row, { className: 'no-gutter boxed message-box' },
                                React.createElement(Col, null,
                                    React.createElement("h3", { id: 'box-heading' },
                                        React.createElement(Icon, { icon: 'warning-large', iconSize: 1 }),
                                        "You are part of a team assigned to this task"),
                                    React.createElement("p", { id: 'box-text' },
                                        React.createElement(ControlGroup, { id: 'toggle-exclusion', control: 'checkbox', type: 'styled', label: null },
                                            React.createElement(ControlOption, { checked: !selectedUserIds.includes(currentUser.id), onChange: () => {
                                                    const nextUsers = toggleArrayItem(selectedUserIds, currentUser.id);
                                                    onTaskEdited('excludeUser', !task.excludeUser.value);
                                                    onTaskEdited('users', nextUsers);
                                                } })),
                                        "Exclude myself from this task")))))),
                    React.createElement(Tab, { eventKey: 'add-user', title: React.createElement("div", null,
                            "Individuals",
                            React.createElement("span", { className: 'activeArrow', onClick: () => setIsDropdownOpen((prev) => !prev) },
                                React.createElement("i", { className: 'icon-chevron-down-large' }))) },
                        React.createElement("form", null,
                            React.createElement(SortableTable, { className: 'members-table', columns: [
                                    {
                                        name: 'edit',
                                        display: () => '',
                                        className: () => 'edit'
                                    },
                                    {
                                        name: 'name',
                                        display: () => 'Name',
                                        className: () => 'name'
                                    },
                                    {
                                        name: 'team',
                                        display: () => 'Team',
                                        className: () => 'team'
                                    }
                                ], rows: individualRows }),
                            hasMoreMembersToLoad && (React.createElement(Row, null,
                                React.createElement(Col, { xs: 4, xsOffset: 4 },
                                    React.createElement(Button, { className: 'full', onClick: handleLoadMoreMembers }, isFetchingTeamMembers ? React.createElement(Loading, null) : 'Load More')))))))))));
};
const mapStateToProps = (state) => ({
    business: businessSelector(state),
    currentUser: currentUserSelector(state),
    queryMembersResults: queryTeamMembersSelector(state),
    teams: businessTeamsSelector(state),
    isFetchingTeams: state.business.isFetchingTeams,
    isFetchingTeamMembers: state.business.isFetchingTeamMembers
});
const mapDispatchToProps = (dispatch) => ({
    getTeams: bindActionCreators(businessActions.business.team.getTeams, dispatch),
    loadMoreTeams: bindActionCreators(businessActions.business.team.loadMoreTeams, dispatch),
    getTeamMembers: bindActionCreators(businessActions.business.queryTeamMembers, dispatch),
    loadMoreMembers: bindActionCreators(businessActions.business.loadMoreTeamMembers, dispatch)
});
export default connect(mapStateToProps, mapDispatchToProps)(AddMembers);



//////////////////
// WEBPACK FOOTER
// ./src/containers/pages/app/tasks/partials/add-members.tsx
// module id = 2388
// module chunks = 0